import React, {
  useEffect,
  useState,
  useMemo,
  useContext,
  useCallback,
} from "react";
import { RouteComponentProps } from "react-router";
import { Box, Typography } from "@material-ui/core";
import {
  Icon,
  IconName,
  GenericAddOnItem as AddOnItem,
  ErrorOutsideComponentType,
  useDeviceTypes,
  getRewardText,
  CorpPolicyBanner,
  getCfarAirDetailsRefundString,
  getRewardsString,
  B2BSpinner,
  HtsLoadError,
  SanitizedHtml,
} from "halifax";
import {
  CallState,
  CFAR_ADD_ON_CHOICE,
  DISRUPTION_ADD_ON_CHOICE,
  Cap1DisruptionAddOnChoiceProperties,
  CAP1_VIEW_BOOKING_ADD_ON,
  DISRUPTION_PROTECTION_HEADER,
  CorporateTravel,
  CorpFareDetails,
} from "redmond";
import clsx from "clsx";
import { useShowPolicyBanner, getFdaDisplayName } from "@capone/common";
import { useBusinessExperimentsById } from "@capone/experiments";
import { ClientContext } from "../../../../../App";
import { Progress, CheckoutSteps } from "../../../../book/reducer/selectors";
import { AGENT_FEE } from "../../../../book/components";
import { trackEvent } from "../../../../../api/v0/analytics/trackEvent";
import { Cap1AirCfarHtsMerchandising, CfarDetails } from "../cfar";

import { DisruptionProtectionDetails } from "../disruptionProtection";
import { AddOnPricingBreakdown } from "../AddOnPricingBreakdown";
import { AddOnCustomizeConnectorProps } from "./container";
import * as constants from "./constants";
import "./styles.scss";
import {
  AVAILABLE,
  SEAT_SELECTION,
  getExperimentVariant,
  useExperiments,
  FLIGHT_LIST_OPTIMIZATION_V1_EXPERIMENT,
  CFAR_SOCIAL_SOCIAL_PROOF_TESTIMONY,
  CFAR_SOCIAL_SOCIAL_PROOF_TESTIMONY_CFAR_FDA,
  XMOD_V1_EXPERIMENT,
} from "../../../../../context/experiments";
import { AddOnTestimonyCarousel } from "../AddOnTestimonyCarousel";

import { getFormattedTotalPriceText } from "../helpers";
import { Cap1AirDisruptionHtsMerchandising } from "../disruptionProtection/Cap1AirDisruptionHtsMerchandising";
import { useHtsAirFlexibility } from "../hooks/useHtsAirFlexibility";
import { useHtsMerchandisingScripts } from "../hooks/useHtsMerchandisingScripts";

export interface IAddOnCustomizeProps
  extends AddOnCustomizeConnectorProps,
    RouteComponentProps {
  // note: this callback function is used to overwrite the default (successful) on-continue behaviour
  onContinueSuccess?: () => void;
  hideGoToCheckoutButton?: boolean;
  hidePricingBreakdown?: boolean;
  useFlightCheckoutVariant?: boolean;
  disableCollapse?: boolean;
}

enum AncillaryType {
  Cfar,
  DisruptionProtection,
}

export const AddOnCustomize = (props: IAddOnCustomizeProps) => {
  const {
    isCfarAvailable,
    selectedCfarId,
    cfarOfferPrices,
    isDisruptionProtectionAvailable,
    selectedDisruptionProtectionId,
    disruptionProtectionOfferPrices,
    rewardsKey,
    isOptionSelectionComplete,
    isDpOptionSelectionComplete,
    isCfarOptionSelectionComplete,
    history,
    populateFlightBookQueryParams,
    fetchAncillaryOffer,
    onContinueSuccess = () => {
      populateFlightBookQueryParams({ history });
    },
    getDpOptionSelectionProgress,
    getCfarOptionSelectionProgress,
    getCheckoutStepNumber,
    scheduleQuote,
    setSelectedCfarId,
    setSelectedDisruptionProtectionId,
    fetchAncillaryOfferCallState,
    cfarAddOnChoiceProperties,
    dpAddOnChoiceProperties,
    viewBookingAddOnProperties,
    dpOfferPropertiesForViewBookingAddOn,
    cfarOfferPropertiesForViewBookingAddOn,
    hideGoToCheckoutButton,
    useFlightCheckoutVariant,
    hidePricingBreakdown = useFlightCheckoutVariant,
    disableCollapse = false,
    hasActiveRefundableFare,
    cfarSocialVariant,
    cfarDiscountPromoCopy,
    discountedCfarOffer,
    cfarOffer,
    cfarDiscountProperties,
    isAirOfferRedesignEnabled,
    isCfarCoMerchEnabled,
    selectedCfarOfferCoverage,
    selectedFare,
    hasTravelFusionFareBrand,
    disruptionOffer,
    fdaProductVariant,
  } = props;
  const { matchesMobile } = useDeviceTypes();
  const clientContext = useContext(ClientContext);
  const { isAgentPortal } = clientContext;
  const [isCfarCollapsed, setIsCfarCollapsed] = useState(false);
  const [cfarError, setCfarError] = useState<ErrorOutsideComponentType>();
  const [isDisruptionProtectionCollapsed, setIsDisruptionProtectionCollapsed] =
    useState(false);
  const [disruptionProtectionError, setDisruptionProtectionError] =
    useState<ErrorOutsideComponentType>();
  const [openCfarModal, setOpenCfarModal] = useState(false);

  const expState = useExperiments();

  const { isHtsCfar, htsCfarOffer, isHtsDisruption, htsDisruptionOffer } =
    useHtsAirFlexibility({
      disruptionOffer,
      cfarOffer,
      hasActiveRefundableFare,
    });

  const seatSelectionGroup = getExperimentVariant(
    expState.experiments,
    SEAT_SELECTION
  );

  const isSeatSelectionGroupEnabled = seatSelectionGroup === AVAILABLE;

  const hideIconColumn = useFlightCheckoutVariant;

  const isFlightListOptimizationExperiment = useMemo(
    () =>
      !matchesMobile &&
      getExperimentVariant(
        expState.experiments,
        FLIGHT_LIST_OPTIMIZATION_V1_EXPERIMENT
      ) === AVAILABLE,
    [expState]
  );

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const freeFdaVariant = useBusinessExperimentsById("corp-free-fda")?.variant;
  const isFreeFdaEnabled =
    freeFdaVariant === "available" || freeFdaVariant === "available_phase2";

  const fdaDisplayName = getFdaDisplayName({
    isFreeFdaEnabled,
    productVariant: fdaProductVariant,
  });

  const isDisruptionProtectionDisabled =
    useFlightCheckoutVariant &&
    getDpOptionSelectionProgress(isSeatSelectionGroupEnabled)?.status ===
      Progress.NOT_STARTED;
  const isCfarDisabled =
    useFlightCheckoutVariant &&
    getCfarOptionSelectionProgress(isSeatSelectionGroupEnabled)?.status ===
      Progress.NOT_STARTED;

  const cfarDiscount =
    cfarOffer && cfarOffer.length > 0 ? cfarOffer[0].discount : undefined;
  const cfarDiscountEnabled = cfarDiscount !== undefined;

  const hasError = useMemo(
    () =>
      cfarError === ErrorOutsideComponentType.NoOptionSelected ||
      disruptionProtectionError === ErrorOutsideComponentType.NoOptionSelected,
    [cfarError, disruptionProtectionError]
  );

  const handleCfarAddOnChoiceEvent = () => {
    trackEvent({
      eventName: CFAR_ADD_ON_CHOICE,
      properties: {
        ...cfarAddOnChoiceProperties,
        ...{
          cfar_discount_original_premium:
            cfarDiscountProperties.original_premium,
          cfar_discount_percentage: cfarDiscountProperties.discount_percentage,
        },
        success: true,
      },
    });
  };

  const handleDpAddOnChoiceEvent = () => {
    trackEvent({
      eventName: DISRUPTION_ADD_ON_CHOICE,
      properties: {
        ...dpAddOnChoiceProperties,
      } as Cap1DisruptionAddOnChoiceProperties,
    });
  };

  const handleOnContinue = () => {
    if (isOptionSelectionComplete) {
      handleCfarAddOnChoiceEvent();
      handleDpAddOnChoiceEvent();
      onContinueSuccess();
    } else {
      if (!selectedCfarId) {
        setCfarError(ErrorOutsideComponentType.NoOptionSelected);
      }
      if (!selectedDisruptionProtectionId) {
        setDisruptionProtectionError(
          ErrorOutsideComponentType.NoOptionSelected
        );
      }
    }
  };

  const resetCfarSelection = useCallback(() => {
    setSelectedCfarId(null);
  }, [setSelectedCfarId]);

  const resetDisruptionSelection = useCallback(() => {
    setSelectedDisruptionProtectionId(null);
  }, [setSelectedDisruptionProtectionId]);

  const {
    isHtsFlexibilityLoaded,
    htsLoadingError,
    htsCfarLoadingError,
    htsDisruptionLoadingError,
    htsCfarRetryCount,
    htsDisruptionRetryCount,
    onHtsCfarLoadSuccess,
    onHtsDisruptionLoadSuccess,
    onHtsCfarLoadError,
    onHtsDisruptionLoadError,
    onHtsCfarRetry,
    onHtsDisruptionRetry,
  } = useHtsMerchandisingScripts({
    isHtsCfar,
    isHtsDisruption,
    isCfarAvailable: !hasActiveRefundableFare && isCfarAvailable,
    isDisruptionProtectionAvailable,
    resetCfarSelection,
    resetDisruptionSelection,
  });

  const handleHtsRetry = useCallback(() => {
    onHtsCfarRetry();
    onHtsDisruptionRetry();
  }, [onHtsCfarRetry, onHtsDisruptionRetry]);

  const isAncillaryDisplayed = (type: AncillaryType): boolean => {
    const isAncillaryAvailable = (() => {
      switch (type) {
        case AncillaryType.DisruptionProtection:
          return isDisruptionProtectionAvailable;
        case AncillaryType.Cfar:
          return isCfarAvailable;
        default:
          return false;
      }
    })();

    return isAncillaryAvailable;
  };

  useEffect(() => {
    /**
     * note: after the user refreshes the page on flight checkout, and clicks on "customize" in the progress bar,
     * it needs to re-fetch the CFAR offer
     */
    if (fetchAncillaryOfferCallState === CallState.NotCalled) {
      fetchAncillaryOffer({
        preserveCfarId: hasActiveRefundableFare,
      });
    }
  }, [fetchAncillaryOfferCallState, hasActiveRefundableFare]);

  const availableAncillariesList: AncillaryType[] = [
    isDisruptionProtectionAvailable ? [AncillaryType.DisruptionProtection] : [],
    isCfarAvailable ? [AncillaryType.Cfar] : [],
  ].flat();

  useEffect(() => {
    if (
      fetchAncillaryOfferCallState === CallState.Success &&
      (isCfarAvailable || isDisruptionProtectionAvailable)
    ) {
      trackEvent({
        eventName: CAP1_VIEW_BOOKING_ADD_ON,
        properties: {
          ...viewBookingAddOnProperties,
          ...(isDisruptionProtectionAvailable &&
            dpOfferPropertiesForViewBookingAddOn),
          ...(isCfarAvailable && cfarOfferPropertiesForViewBookingAddOn),
          ...{
            cfar_discount_original_premium:
              cfarDiscount?.originalPremiumAmount.fiat.value,
            cfar_discount_percentage: cfarDiscount?.discountPercentage,
          },
        },
      });
    }
  }, [
    fetchAncillaryOfferCallState,
    isCfarAvailable,
    isDisruptionProtectionAvailable,
  ]);

  useEffect(() => {
    if (
      useFlightCheckoutVariant &&
      ((isDisruptionProtectionAvailable && isDpOptionSelectionComplete) ||
        (isCfarAvailable && isCfarOptionSelectionComplete))
    ) {
      scheduleQuote({
        agentFee: isAgentPortal ? AGENT_FEE : 0,
        pollQuoteOnly: true,
        preserveQuote: true,
      });
    }
  }, [
    useFlightCheckoutVariant,
    isDisruptionProtectionAvailable,
    isDpOptionSelectionComplete,
    isCfarAvailable,
    isCfarOptionSelectionComplete,
    selectedDisruptionProtectionId,
    selectedCfarId,
  ]);

  const getCfarBannerContent = () => {
    if (cfarDiscountEnabled && !isAirOfferRedesignEnabled) {
      return `${cfarDiscountPromoCopy} limited-time promotion`;
    }
    return undefined;
  };

  const renderCfarAddOnItem = () => {
    if (isHtsCfar) {
      return (
        <Cap1AirCfarHtsMerchandising
          cfarOffer={htsCfarOffer}
          onLoadSuccess={onHtsCfarLoadSuccess}
          onLoadError={onHtsCfarLoadError}
          isLoading={!isHtsFlexibilityLoaded}
          isError={htsCfarLoadingError}
          retryCount={htsCfarRetryCount}
        />
      );
    }
    const getCollapsedCopy = () => {
      const price =
        selectedFare?.paxPricings?.[0].pricing.baseAmount.fiat ?? undefined;
      const tax = selectedFare?.paxPricings?.[0].pricing.taxAmount.fiat;
      if (!price) {
        return undefined;
      }
      const refundAmount = getCfarAirDetailsRefundString({
        price,
        tax,
        coverage: selectedCfarOfferCoverage?.cashCoveragePercentage!,
      });
      return isCfarCoMerchEnabled && cfarOfferPrices
        ? `Added ${
            selectedCfarOfferCoverage?.cashCoveragePercentage
          }% (${refundAmount} refund per traveler) coverage for ${constants.getPriceAndRewardsCopy(
            {
              price: getFormattedTotalPriceText(cfarOfferPrices.fiat),
              rewards: rewardsKey
                ? getRewardsString(cfarOfferPrices.rewards?.[rewardsKey])
                : "",
            }
          )} per traveler.`
        : undefined;
    };
    return (
      <>
        <AddOnItem
          key={`cfar-hidden:${!isHtsFlexibilityLoaded}`}
          hidden={!isHtsFlexibilityLoaded}
          type="cancel-for-any-reason"
          header={
            <>
              <Typography variant="inherit" component="span">
                Cancel{" "}
              </Typography>
              <Typography variant="inherit" component="span">
                for any reason
              </Typography>
            </>
          }
          headerStyle="default"
          subtitle={constants.CFAR_SUBTITLE_COPY}
          iconName={IconName.CheckShieldNoCircle}
          headerBannerContent={getCfarBannerContent()}
          onHeaderBannerClick={() => setOpenCfarModal(true)}
          content={
            <CfarDetails
              isAgentPortal={isAgentPortal}
              contentOnly
              cardContentProps={{
                hideTopContent: true,
                hideSubtitle: true,
                hideContinueCta: true,
                disablePartialScroll: true,
                readTermsAndConditionsLinkPosition: "left",
                onSelectValueCallback: () => {
                  setCfarError(undefined);
                },
                onConfirmSelectedValueCallback: () => {
                  setTimeout(() => {
                    setIsCfarCollapsed(true);
                  }, 800);
                },
                error: cfarError,
              }}
              disabled={isCfarDisabled}
              hasTravelFusionFareBrand={hasTravelFusionFareBrand}
            />
          }
          fetchAncillaryOfferCallState={fetchAncillaryOfferCallState}
          error={cfarError}
          isCollapsed={isCfarCollapsed}
          setIsCollapsed={setIsCfarCollapsed}
          hasSelectedOption={!!selectedCfarId}
          priceCopy={
            cfarOfferPrices
              ? cfarDiscountEnabled && discountedCfarOffer
                ? constants.getDiscountedPriceAndRewardsCopy({
                    origPrice: getFormattedTotalPriceText(
                      discountedCfarOffer.originalPremiumAmount.fiat
                    ),
                    discountedPrice: getFormattedTotalPriceText(
                      cfarOfferPrices.fiat
                    ),
                    rewards: rewardsKey
                      ? getRewardText({
                          reward: cfarOfferPrices.rewards[rewardsKey],
                        })
                      : undefined,
                  })
                : constants.getPriceAndRewardsCopy({
                    price: getFormattedTotalPriceText(cfarOfferPrices.fiat),
                    rewards: rewardsKey
                      ? getRewardsString(cfarOfferPrices.rewards?.[rewardsKey])
                      : "",
                  })
              : undefined
          }
          collapsedCopy={getCollapsedCopy()}
          disableCollapse={disableCollapse}
          hideIconColumn={hideIconColumn}
          isInlineIconHeader={false}
          disabled={isCfarDisabled}
          redesignClassName={
            isAirOfferRedesignEnabled
              ? constants.AIR_OFFER_REDESIGN_CLASSNAME
              : undefined
          }
        />
        <CfarDetails
          isAgentPortal={isAgentPortal}
          openCfarDetails={openCfarModal}
          onClose={() => setOpenCfarModal(false)}
          contentOnly={false}
          modalType="refundable-fare"
          topBannerEnabled
          cardContentProps={{ hideContinueCta: true, hideRadioButtons: true }}
          hasTravelFusionFareBrand={hasTravelFusionFareBrand}
        />
      </>
    );
  };

  const renderDisruptionProtectionAddOnItem = () => {
    if (isHtsDisruption) {
      return (
        <Cap1AirDisruptionHtsMerchandising
          disruptionOffer={htsDisruptionOffer}
          selectedDisruptionProtectionId={selectedDisruptionProtectionId}
          onLoadSuccess={onHtsDisruptionLoadSuccess}
          onLoadError={onHtsDisruptionLoadError}
          isLoading={!isHtsFlexibilityLoaded}
          isError={htsDisruptionLoadingError}
          retryCount={htsDisruptionRetryCount}
        />
      );
    }

    const disruptionProtectionHeaderStyle = useFlightCheckoutVariant
      ? "checkout"
      : "contrast";

    const Header = () => (
      <SanitizedHtml
        as={Typography}
        variant="inherit"
        html={useFlightCheckoutVariant
          ? constants.getStepCopy(
              getCheckoutStepNumber(CheckoutSteps.DISRUPTION_SUPPORT),
              DISRUPTION_PROTECTION_HEADER({ useStrong: !isXmodRedesign })
            )
          : DISRUPTION_PROTECTION_HEADER({ useStrong: !isXmodRedesign })}
      />
    );

    const getHeader = () => (
      <>
        {useFlightCheckoutVariant ? (
          <>
            <Icon
              name={IconName.DisruptionProtectionNoCircle}
              className={clsx("header-icon", "disruption-protection")}
            />
            <Header />
          </>
        ) : (
          <Header />
        )}
      </>
    );

    const priceCopy = disruptionProtectionOfferPrices
      ? constants.getPriceAndRewardsCopy({
          price: getFormattedTotalPriceText(
            disruptionProtectionOfferPrices.fiat
          ),
          rewards: rewardsKey
            ? getRewardsString(
                disruptionProtectionOfferPrices.rewards?.[rewardsKey]
              )
            : "",
        })
      : undefined;

    const subtitle = (
      <Typography variant="body2" className="card-subtitle-copy">
        {constants.DISRUPTION_PROTECTION_SUBTITLE_COPY}
      </Typography>
    );

    return (
      <AddOnItem
        key={`disruption-protection-hidden:${!isHtsFlexibilityLoaded}`}
        hidden={!isHtsFlexibilityLoaded}
        type={fdaDisplayName}
        header={getHeader()}
        headerStyle={disruptionProtectionHeaderStyle}
        subtitle={
          matchesMobile || isAirOfferRedesignEnabled ? subtitle : undefined
        }
        iconName={IconName.DisruptionProtectionNoCircle}
        content={
          <DisruptionProtectionDetails
            contentOnly
            cardContentProps={{
              hideTopContent: true,
              hideSubtitle: true,
              hideContinueCta: true,
              disablePartialScroll: true,
              readTermsAndConditionsLinkPosition: "left",
              onSelectValueCallback: () => {
                setDisruptionProtectionError(undefined);
              },
              onConfirmSelectedValueCallback: () => {
                setTimeout(() => {
                  setIsDisruptionProtectionCollapsed(true);
                }, 800);
              },
              error: disruptionProtectionError,
            }}
            disabled={isDisruptionProtectionDisabled}
          />
        }
        fetchAncillaryOfferCallState={fetchAncillaryOfferCallState}
        error={disruptionProtectionError}
        isCollapsed={isDisruptionProtectionCollapsed}
        setIsCollapsed={setIsDisruptionProtectionCollapsed}
        hasSelectedOption={!!selectedDisruptionProtectionId}
        priceCopy={priceCopy}
        disableCollapse={disableCollapse}
        hideIconColumn={hideIconColumn}
        isInlineIconHeader={false}
        disabled={isDisruptionProtectionDisabled}
        redesignClassName={
          isAirOfferRedesignEnabled
            ? constants.AIR_OFFER_REDESIGN_CLASSNAME
            : undefined
        }
      />
    );
  };

  const addOnItems: (JSX.Element | null)[] = availableAncillariesList.map(
    (ancillaryType) => {
      if (!isAncillaryDisplayed(ancillaryType)) {
        return null;
      }

      switch (ancillaryType) {
        case AncillaryType.Cfar:
          return renderCfarAddOnItem();
        case AncillaryType.DisruptionProtection:
          return renderDisruptionProtectionAddOnItem();

        default:
          return null;
      }
    }
  );

  const hasDisruptionProtectionAddOnToShow = isAncillaryDisplayed(
    AncillaryType.DisruptionProtection
  );
  const hasCfarAddOnToShow = isAncillaryDisplayed(AncillaryType.Cfar);

  return (
    <Box className={clsx("add-on-customize-root", { xmod: isXmodRedesign })}>
      <Box
        className={clsx("add-on-customize-container", {
          "full-size": useFlightCheckoutVariant,
          "flight-list-optimization-experiment":
            isFlightListOptimizationExperiment,
        })}
      >
        <Box className="add-on-items-section">
          {!isHtsFlexibilityLoaded && (
            <B2BSpinner
              className="hts-desktop-loading-spinner"
              style={{ width: "48px", height: "48px" }}
            />
          )}
          {htsLoadingError && (
            <HtsLoadError
              header={constants.HtsErrorHeader}
              message={constants.HtsErrorMessage}
              handleRetry={handleHtsRetry}
            />
          )}
          {addOnItems}
        </Box>
        {!hidePricingBreakdown && (
          <Box className="checkout-breakdown-section">
            <AddOnCheckoutBreakdown
              disabled={!isOptionSelectionComplete}
              onContinue={handleOnContinue}
              hideGoToCheckoutButton={hideGoToCheckoutButton}
              isMobile={matchesMobile}
              hasError={hasError}
              corporateTravel={
                (selectedFare as CorpFareDetails)?.corporateTravel
              }
            />
          </Box>
        )}
      </Box>
      {cfarSocialVariant === CFAR_SOCIAL_SOCIAL_PROOF_TESTIMONY_CFAR_FDA && (
        <AddOnTestimonyCarousel
          hasCfarAddOnToShow={hasCfarAddOnToShow}
          hasDisruptionProtectionAddOnToShow={
            hasDisruptionProtectionAddOnToShow
          }
          showArrows
        />
      )}

      {cfarSocialVariant === CFAR_SOCIAL_SOCIAL_PROOF_TESTIMONY && (
        <AddOnTestimonyCarousel
          // For the DP only variant, this carousel element behaves as if CFAR is not offered (thus no CFAR testimonies).
          hasCfarAddOnToShow={false}
          hasDisruptionProtectionAddOnToShow={
            hasDisruptionProtectionAddOnToShow
          }
          showArrows
        />
      )}
    </Box>
  );
};

interface IAddOnCheckoutBreakdownProps {
  disabled?: boolean;
  hideGoToCheckoutButton?: boolean;
  onContinue: () => void;
  isMobile?: boolean;
  hasError: boolean;
  corporateTravel: CorporateTravel | undefined;
}

const AddOnCheckoutBreakdown = (props: IAddOnCheckoutBreakdownProps) => {
  const {
    disabled,
    hideGoToCheckoutButton,
    onContinue,
    isMobile,
    hasError,
    corporateTravel,
  } = props;

  const onClickWhenDisabled = () => {
    onContinue();
    const errorSection =
      document.querySelector(".disruption-protection-details-error-message") ??
      document.querySelector(".cfar-details-error-message");
    if (errorSection) errorSection.scrollIntoView({ behavior: "smooth" });
  };

  const { policies } = useContext(ClientContext);
  const showPolicyBanner = useShowPolicyBanner(policies);

  return (
    <Box className="add-on-checkout-breakdown-root">
      <CorpPolicyBanner
        variant="base"
        corporateTravel={showPolicyBanner ? corporateTravel : undefined}
        productType="flight"
      />
      <Box className="add-on-checkout-breakdown-container">
        <AddOnPricingBreakdown
          actionButtonProps={
            !hideGoToCheckoutButton
              ? {
                  message: constants.CONTINUE_TO_CHECKOUT_COPY,
                  disabled,
                  onContinue,
                  onClickWhenDisabled,
                }
              : undefined
          }
          isMobile={isMobile}
          hasError={hasError}
        />
      </Box>
    </Box>
  );
};
